// Quiz Creator Component - Main quiz creation interface
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { ArrowLeft } from "lucide-react";
import {
  Quiz,
  QuizSchedule,
  QuizSettings,
  QuizGrading,
} from "../../../types/quiz";
// Question types are provided via the question builder hook
import quizService from "../../../services/quizService";
import courseService from "../../../services/courseService";
// Import from the local index barrel to avoid module resolution issues
import { QuizScheduler, QuestionBuilder } from "./index";
import { useQuestionBuilder } from "../../../hooks/useQuestionBuilder";
import { useRef } from "react";

interface QuizCreatorProps {
  courseId: string;
  quizId?: string;
  onQuizCreated?: (quiz: Quiz) => void;
}

const QuizCreator: React.FC<QuizCreatorProps> = ({
  courseId,
  quizId: _quizId,
  onQuizCreated,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState<any[]>([]);
  const [step, setStep] = useState<
    "basic" | "questions" | "settings" | "schedule" | "review"
  >("basic");

  const [quiz, setQuiz] = useState<Partial<Quiz>>({
    title: "",
    description: "",
    instructions: "",
    courseId: courseId || "",
    timeLimit: 60,
    attempts: 1,
    passingScore: 60,
    shuffleQuestions: false,
    shuffleOptions: false,
    showResults: true,
    showCorrectAnswers: true,
    questions: [],
    settings: {
      randomizeQuestions: false,
      oneQuestionAtATime: false,
      lockQuestionsAfterAnswering: false,
      requireWebcam: false,
      fullScreenMode: false,
      showProgressBar: true,
    },
    grading: {
      autoGrade: true,
      gradingMethod: "latest",
      releaseGrades: "immediately",
      releaseReview: "immediately",
    },
  });

  // Debug state to surface what the loader fetched (useful when console isn't visible)
  const [fetchDebug, setFetchDebug] = useState<{
    raw?: any;
    normalized?: any;
    error?: string | null;
  }>({ raw: undefined, normalized: undefined, error: null });

  // questionsCount removed; using qbQuestions.length directly
  // Question builder hook (single source of truth)
  const questionApi = useQuestionBuilder();

  const redirectTimerRef = useRef<number | null>(null);

  // Persistence functions
  const saveQuizDataToStorage = (
    quizData: Partial<Quiz>,
    questionsData: any[]
  ) => {
    const storageKey = `quiz_draft_${courseId || "new"}`;
    const dataToSave = {
      quiz: quizData,
      questions: questionsData,
      step: step,
      timestamp: Date.now(),
      courseId: courseId,
    };
    localStorage.setItem(storageKey, JSON.stringify(dataToSave));
  };

  const loadQuizDataFromStorage = () => {
    const storageKey = `quiz_draft_${courseId || "new"}`;
    const storedData = localStorage.getItem(storageKey);

    if (!storedData) return null;

    try {
      const parsed = JSON.parse(storedData);
      const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;

      // Check if data is expired (older than 7 days)
      if (Date.now() - parsed.timestamp > sevenDaysInMs) {
        localStorage.removeItem(storageKey);
        return null;
      }

      return parsed;
    } catch (error) {
      console.error("Failed to parse stored quiz data:", error);
      localStorage.removeItem(storageKey);
      return null;
    }
  };

  const clearQuizDataFromStorage = () => {
    const storageKey = `quiz_draft_${courseId || "new"}`;
    localStorage.removeItem(storageKey);
  };

  const {
    currentQuestion,
    questions: qbQuestions,
    errors: qbErrors,
    updateQuestion,
    updateTypeSpecificData,
    changeQuestionType,
    addQuestion,
    updateQuestionAtIndex,
    removeQuestion,
    moveQuestion,
    loadQuestions,
    getTotalPoints,
    getEstimatedTime,
    clearError,
  } = questionApi;

  // Load persisted data on mount (only for new quizzes, not edits)
  useEffect(() => {
    if (!_quizId) {
      const storedData = loadQuizDataFromStorage();
      if (storedData) {
        setQuiz(storedData.quiz);
        setStep(storedData.step);
        if (
          storedData.questions &&
          Array.isArray(storedData.questions) &&
          loadQuestions
        ) {
          loadQuestions(storedData.questions);
        }
        toast.success(
          "Draft quiz data loaded from previous session - you can continue where you left off"
        );
      }
    }
  }, [_quizId, loadQuestions]);

  // Save data to localStorage whenever quiz data changes (only for new quizzes)
  useEffect(() => {
    if (!_quizId && quiz.title) {
      // Only save if we have at least a title
      saveQuizDataToStorage(quiz, qbQuestions);
    }
  }, [quiz, qbQuestions, step, _quizId]);

  // NOTE: questions are synced from the question builder hook via useEffect below

  // Keep quiz.questions and questionsCount in sync with the hook-managed questions (single source)
  useEffect(() => {
    if (qbQuestions && Array.isArray(qbQuestions)) {
      setQuiz((prev) => ({ ...prev, questions: qbQuestions }));
    }
  }, [qbQuestions]);

  // Load courses for selection
  useEffect(() => {
    const loadCourses = async () => {
      try {
        const response = await courseService.getAllCoursesForQuiz();
        setCourses(response.data || []);
      } catch (error) {
        console.error("Failed to load courses:", error);
        toast.error("Failed to load courses");
      }
    };
    loadCourses();
  }, []);

  const handleBasicInfoChange = (field: keyof Quiz, value: any) => {
    setQuiz((prev) => ({ ...prev, [field]: value }));
  };

  const handleSettingsChange = (settings: QuizSettings) => {
    setQuiz((prev) => ({ ...prev, settings }));
  };

  const handleGradingChange = (grading: QuizGrading) => {
    setQuiz((prev) => ({ ...prev, grading }));
  };

  const handleScheduleChange = (schedule: QuizSchedule) => {
    setQuiz((prev) => ({ ...prev, schedule }));
  };

  const validateBasicInfo = (): boolean => {
    if (!quiz.title?.trim()) {
      toast.error("Quiz title is required");
      return false;
    }
    if (!quiz.courseId?.trim()) {
      toast.error("Course selection is required");
      return false;
    }
    if (!quiz.timeLimit || quiz.timeLimit <= 0) {
      toast.error("Time limit must be greater than 0");
      return false;
    }
    if (
      !quiz.passingScore ||
      quiz.passingScore < 0 ||
      quiz.passingScore > 100
    ) {
      toast.error("Passing score must be between 0 and 100");
      return false;
    }
    return true;
  };

  const validateQuestions = (): boolean => {
    if (!qbQuestions || qbQuestions.length === 0) {
      toast.error("At least one question is required");
      return false;
    }
    return true;
  };

  const validateSchedule = (): boolean => {
    if (quiz.schedule?.availableFrom && quiz.schedule?.availableUntil) {
      const start = new Date(quiz.schedule.availableFrom);
      const end = new Date(quiz.schedule.availableUntil);
      if (start >= end) {
        toast.error("Available until date must be after available from date");
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    switch (step) {
      case "basic":
        if (validateBasicInfo()) setStep("questions");
        break;
      case "questions":
        if (validateQuestions()) setStep("settings");
        break;
      case "settings":
        setStep("schedule");
        break;
      case "schedule":
        if (validateSchedule()) setStep("review");
        break;
      default:
        break;
    }
  };

  const handlePrevious = () => {
    switch (step) {
      case "questions":
        setStep("basic");
        break;
      case "settings":
        setStep("questions");
        break;
      case "schedule":
        setStep("settings");
        break;
      case "review":
        setStep("schedule");
        break;
      default:
        break;
    }
  };

  const handleSubmit = async () => {
    if (!validateBasicInfo() || !validateQuestions() || !validateSchedule()) {
      return;
    }

    setLoading(true);
    try {
      // Build payload with required fields and current question list
      const quizData = {
        title: quiz.title || "",
        description: quiz.description || "",
        instructions: quiz.instructions || "",
        courseId: quiz.courseId || "",
        timeLimit: quiz.timeLimit || 60,
        attempts: quiz.attempts || 1,
        passingScore: quiz.passingScore || 0,
        shuffleQuestions: quiz.shuffleQuestions || false,
        shuffleOptions: quiz.shuffleOptions || false,
        showResults: quiz.showResults || true,
        showCorrectAnswers: quiz.showCorrectAnswers || true,
        questions: qbQuestions || [],
        settings: quiz.settings || {},
        grading: quiz.grading || {},
        schedule: quiz.schedule || {},
      };

      let result;
      if (_quizId) {
        // Update existing quiz
        result = await quizService.updateQuiz(_quizId, quizData as any);
        toast.success("Quiz updated successfully!");
      } else {
        // Create new quiz
        result = await quizService.createQuiz(quizData as any);
        toast.success("Quiz created successfully!");
      }

      // Response shape may vary; normalize to an object containing the quiz
      const normalized = result && typeof result === "object" ? result : null;

      // Try several common places for the id
      const quizId =
        normalized?._id ||
        (normalized as any)?.id ||
        (normalized as any)?.data?._id ||
        (normalized as any)?.data?.id ||
        _quizId; // fallback to current quiz id if updating

      onQuizCreated?.(result as any);

      if (quizId) {
        if (_quizId) {
          // After updating, optionally refresh the current edit page or just stay
          // No navigation needed since we're already on the edit page
          // navigate(`/elearning/teacher/quizzes/${quizId}/edit`);
        } else {
          // Clear stored draft data after successful creation
          clearQuizDataFromStorage();

          // Navigate directly to quizzes list
          navigate(`/elearning/quizzes`);
        }
      } else {
        // Fallback: no id returned, navigate to quizzes list anyway
        clearQuizDataFromStorage();
        navigate(`/elearning/quizzes`);
      }
    } catch (error: any) {
      console.error("Error saving quiz:", error);
      const action = _quizId ? "update" : "create";

      // Check for detailed validation errors
      const errorData = error.response?.data;
      if (errorData?.errors && Array.isArray(errorData.errors)) {
        // Display detailed question validation errors
        const errorMessages = errorData.errors.map(
          (err: any) =>
            `Question ${err.questionIndex}: ${err.errors.join(", ")}`
        );
        toast.error(`Validation failed:\n${errorMessages.join("\n")}`);
      } else {
        toast.error(errorData?.message || `Failed to ${action} quiz`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Clear redirect timer on unmount
  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  // If a quizId is provided (edit mode), load the quiz and populate the form + question builder
  useEffect(() => {
    const loadExistingQuiz = async (id: string) => {
      setLoading(true);
      try {
        // The API may return different shapes depending on which service is used
        // Normalize to the quiz object regardless of wrappers
        const raw = await quizService.getQuiz(id as string);

        // Normalize common response shapes:
        // - { success, message, data: { ...quiz } }
        // - { data: { data: { ...quiz } } }
        // - already the quiz object
        let fetched: any = null;
        if (!raw) fetched = null;
        else if ((raw as any).success && (raw as any).data) {
          fetched = (raw as any).data;
        } else if ((raw as any)?.data?.data) {
          fetched = (raw as any).data.data;
        } else if ((raw as any)?.data) {
          fetched = (raw as any).data;
        } else {
          fetched = raw;
        }

        // Save debug details for on-page inspection
        setFetchDebug({ raw, normalized: fetched, error: null });
        if (!fetched) return;

        // Populate quiz basic fields (map schedule to input-friendly local datetime strings)
        const schedule = (fetched as any).schedule || {};

        // Helper function to convert Date to local datetime string for input
        const toLocalDateTimeString = (dateValue: any) => {
          if (!dateValue) return undefined;
          const date = new Date(dateValue);
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          const hours = String(date.getHours()).padStart(2, "0");
          const minutes = String(date.getMinutes()).padStart(2, "0");
          return `${year}-${month}-${day}T${hours}:${minutes}`;
        };

        const mappedSchedule = {
          availableFrom: toLocalDateTimeString(schedule.availableFrom),
          availableUntil: toLocalDateTimeString(schedule.availableUntil),
          dueDate: toLocalDateTimeString(schedule.dueDate),
        };

        // Apply fetched values into local state (defensive defaults)
        setQuiz((prev) => {
          const next = {
            ...prev,
            title:
              (fetched as any).title !== undefined
                ? (fetched as any).title
                : prev.title,
            description:
              (fetched as any).description !== undefined
                ? (fetched as any).description
                : prev.description,
            instructions:
              (fetched as any).instructions !== undefined
                ? (fetched as any).instructions
                : prev.instructions,
            courseId:
              (fetched as any).courseId !== undefined
                ? (fetched as any).courseId
                : prev.courseId,
            moduleId:
              (fetched as any).moduleId !== undefined
                ? (fetched as any).moduleId
                : prev.moduleId,
            timeLimit:
              (fetched as any).timeLimit !== undefined
                ? (fetched as any).timeLimit
                : prev.timeLimit,
            attempts:
              (fetched as any).attempts !== undefined
                ? (fetched as any).attempts
                : prev.attempts,
            passingScore:
              (fetched as any).passingScore !== undefined
                ? (fetched as any).passingScore
                : prev.passingScore,
            shuffleQuestions:
              (fetched as any).shuffleQuestions !== undefined
                ? (fetched as any).shuffleQuestions
                : prev.shuffleQuestions,
            shuffleOptions:
              (fetched as any).shuffleOptions !== undefined
                ? (fetched as any).shuffleOptions
                : prev.shuffleOptions,
            showResults:
              (fetched as any).showResults !== undefined
                ? (fetched as any).showResults
                : prev.showResults,
            showCorrectAnswers:
              (fetched as any).showCorrectAnswers !== undefined
                ? (fetched as any).showCorrectAnswers
                : prev.showCorrectAnswers,
            settings:
              (fetched as any).settings !== undefined
                ? (fetched as any).settings
                : prev.settings,
            grading:
              (fetched as any).grading !== undefined
                ? (fetched as any).grading
                : prev.grading,
            schedule: mappedSchedule,
            questions:
              (fetched as any).questions !== undefined
                ? (fetched as any).questions
                : prev.questions,
          } as Partial<Quiz>;

          return next;
        });

        // Load questions into the question builder hook (questions may be nested)
        const questionsPayload =
          (fetched as any).questions || (raw as any)?.data?.questions || null;
        if (
          Array.isArray(questionsPayload) &&
          typeof loadQuestions === "function"
        ) {
          loadQuestions(questionsPayload as any);
        }

        // Jump to questions step for editing convenience
        setStep("questions");
      } catch (err) {
        console.error("Failed to load quiz for editing:", err);
        setFetchDebug((d) => ({
          ...d,
          error: (err as any)?.message || String(err),
        }));
        toast.error("Failed to load quiz for editing");
      } finally {
        setLoading(false);
      }
    };

    if (_quizId) {
      loadExistingQuiz(_quizId);
    }
  }, [_quizId, loadQuestions]);

  const renderStepContent = () => {
    switch (step) {
      case "basic":
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">
              Basic Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quiz Title *
                </label>
                <input
                  type="text"
                  value={quiz.title}
                  onChange={(e) =>
                    handleBasicInfoChange("title", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter quiz title"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Course *
                </label>
                <select
                  value={quiz.courseId}
                  onChange={(e) =>
                    handleBasicInfoChange("courseId", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select a course</option>
                  {courses.map((course) => (
                    <option key={course._id} value={course._id}>
                      {course.title}({course.level})
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={quiz.description}
                  onChange={(e) =>
                    handleBasicInfoChange("description", e.target.value)
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Describe what this quiz covers"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Instructions
                </label>
                <textarea
                  value={quiz.instructions}
                  onChange={(e) =>
                    handleBasicInfoChange("instructions", e.target.value)
                  }
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Instructions for students taking this quiz"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Time Limit (minutes) *
                </label>
                <input
                  type="number"
                  value={quiz.timeLimit}
                  onChange={(e) =>
                    handleBasicInfoChange("timeLimit", parseInt(e.target.value))
                  }
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Attempts Allowed
                </label>
                <input
                  type="number"
                  value={quiz.attempts}
                  onChange={(e) =>
                    handleBasicInfoChange("attempts", parseInt(e.target.value))
                  }
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Passing Score (%)
                </label>
                <input
                  type="number"
                  value={quiz.passingScore}
                  onChange={(e) =>
                    handleBasicInfoChange(
                      "passingScore",
                      parseInt(e.target.value)
                    )
                  }
                  min="0"
                  max="100"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        );

      case "questions":
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Questions</h3>
              <div className="text-sm text-gray-600">
                {qbQuestions.length} questions •{" "}
                {getTotalPoints ? getTotalPoints() : 0} points • ~
                {Math.ceil(
                  ((getEstimatedTime ? getEstimatedTime() : 0) || 0) / 60
                )}{" "}
                min
              </div>
            </div>
            <QuestionBuilder
              currentQuestion={currentQuestion}
              questions={qbQuestions}
              errors={qbErrors}
              updateQuestion={updateQuestion}
              updateTypeSpecificData={updateTypeSpecificData}
              changeQuestionType={changeQuestionType}
              addQuestion={addQuestion}
              updateQuestionAtIndex={updateQuestionAtIndex}
              removeQuestion={removeQuestion}
              moveQuestion={moveQuestion}
              getTotalPoints={getTotalPoints}
              getEstimatedTime={getEstimatedTime}
              clearError={clearError}
            />
          </div>
        );

      case "settings":
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">
              Quiz Settings
            </h3>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={quiz.settings?.randomizeQuestions}
                    onChange={(e) =>
                      handleSettingsChange({
                        ...quiz.settings,
                        randomizeQuestions: e.target.checked,
                      })
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Randomize Questions
                  </span>
                </label>

                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={quiz.settings?.oneQuestionAtATime}
                    onChange={(e) =>
                      handleSettingsChange({
                        ...quiz.settings,
                        oneQuestionAtATime: e.target.checked,
                      })
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    One Question at a Time
                  </span>
                </label>

                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={quiz.settings?.showProgressBar}
                    onChange={(e) =>
                      handleSettingsChange({
                        ...quiz.settings,
                        showProgressBar: e.target.checked,
                      })
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Show Progress Bar
                  </span>
                </label>

                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={quiz.settings?.requireWebcam}
                    onChange={(e) =>
                      handleSettingsChange({
                        ...quiz.settings,
                        requireWebcam: e.target.checked,
                      })
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Require Webcam
                  </span>
                </label>

                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={quiz.settings?.fullScreenMode}
                    onChange={(e) =>
                      handleSettingsChange({
                        ...quiz.settings,
                        fullScreenMode: e.target.checked,
                      })
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Full Screen Mode
                  </span>
                </label>
              </div>

              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">
                  Grading Options
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Grading Method
                    </label>
                    <select
                      value={quiz.grading?.gradingMethod}
                      onChange={(e) =>
                        handleGradingChange({
                          ...quiz.grading,
                          gradingMethod: e.target.value as
                            | "highest"
                            | "latest"
                            | "average",
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="latest">Latest Attempt</option>
                      <option value="highest">Highest Score</option>
                      <option value="average">Average Score</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Release Grades
                    </label>
                    <select
                      value={quiz.grading?.releaseGrades}
                      onChange={(e) =>
                        handleGradingChange({
                          ...quiz.grading,
                          releaseGrades: e.target.value as
                            | "immediately"
                            | "after_due_date"
                            | "manual",
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="immediately">Immediately</option>
                      <option value="after_due_date">After Due Date</option>
                      <option value="manual">Manual Release</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Release Question Review
                    </label>
                    <select
                      value={quiz.grading?.releaseReview || "immediately"}
                      onChange={(e) =>
                        handleGradingChange({
                          ...quiz.grading,
                          releaseReview: e.target.value as
                            | "immediately"
                            | "after_quiz_closed"
                            | "after_grading"
                            | "never",
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="immediately">
                        Immediately after submission
                      </option>
                      <option value="after_quiz_closed">
                        After quiz is closed
                      </option>
                      <option value="after_grading">
                        After grading is complete
                      </option>
                      <option value="never">Never show review</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Controls when students can see questions, their answers,
                      and correct answers
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case "schedule":
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Schedule</h3>
            <QuizScheduler
              schedule={quiz.schedule}
              onChange={handleScheduleChange}
            />
          </div>
        );

      case "review":
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">
              Review & Create
            </h3>

            <div className="bg-gray-50 p-6 rounded-lg space-y-4">
              <div>
                <h4 className="font-medium text-gray-900">{quiz.title}</h4>
                <p className="text-gray-600">{quiz.description}</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Questions:</span>
                  <span className="ml-2 font-medium">{qbQuestions.length}</span>
                </div>
                <div>
                  <span className="text-gray-500">Total Points:</span>
                  <span className="ml-2 font-medium">
                    {getTotalPoints ? getTotalPoints() : 0}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Time Limit:</span>
                  <span className="ml-2 font-medium">{quiz.timeLimit} min</span>
                </div>
                <div>
                  <span className="text-gray-500">Attempts:</span>
                  <span className="ml-2 font-medium">{quiz.attempts}</span>
                </div>
              </div>

              {quiz.schedule?.availableFrom && (
                <div className="border-t pt-3">
                  <h5 className="font-medium text-gray-900 mb-2">Schedule</h5>
                  <div className="text-sm text-gray-600">
                    <p>
                      Available:{" "}
                      {new Date(quiz.schedule.availableFrom).toLocaleString()}
                    </p>
                    {quiz.schedule.availableUntil && (
                      <p>
                        Until:{" "}
                        {new Date(
                          quiz.schedule.availableUntil
                        ).toLocaleString()}
                      </p>
                    )}
                    {quiz.schedule.dueDate && (
                      <p>
                        Due: {new Date(quiz.schedule.dueDate).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Visible edit-mode banner to confirm loaded quiz when console isn't available */}
      {_quizId && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
          <strong className="mr-2">Edit mode:</strong>
          {quiz.title ? (
            <span>Editing quiz "{quiz.title}"</span>
          ) : (
            <span className="text-gray-600">Loading quiz data...</span>
          )}

          {/* 1````````` */}
        </div>
      )}

      {/* Back Button */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/elearning/quizzes")}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Quizzes
        </button>
      </div>

      {/* Progress Indicator */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {(
            ["basic", "questions", "settings", "schedule", "review"] as const
          ).map((stepName, index) => {
            const stepNames = [
              "basic",
              "questions",
              "settings",
              "schedule",
              "review",
            ] as const;
            const currentStepIndex = stepNames.indexOf(step);
            const isCompleted = index < currentStepIndex;
            const isCurrent = step === stepName;

            return (
              <div
                key={stepName}
                className={`flex items-center ${index < 4 ? "flex-1" : ""}`}
              >
                <span
                  onClick={() => setStep(stepName)}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all cursor-pointer hover:scale-110 ${
                    isCurrent
                      ? "bg-blue-600 text-white ring-2 ring-blue-300"
                      : isCompleted
                      ? "bg-green-600 text-white hover:bg-green-700"
                      : "bg-gray-300 text-gray-600 hover:bg-gray-400"
                  }`}
                  title={`Go to ${
                    stepName.charAt(0).toUpperCase() + stepName.slice(1)
                  }`}
                >
                  {isCompleted ? "✓" : index + 1}
                </span>
                {index < 4 && (
                  <div
                    className={`flex-1 h-0.5 mx-2 ${
                      isCompleted ? "bg-green-600" : "bg-gray-300"
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>
        <div className="flex justify-between mt-2 text-xs font-medium text-gray-600">
          {(
            ["basic", "questions", "settings", "schedule", "review"] as const
          ).map((stepName) => (
            <button
              key={stepName}
              type="button"
              onClick={() => setStep(stepName)}
              className={`hover:text-blue-600 cursor-pointer transition-colors ${
                step === stepName ? "text-blue-600 font-semibold" : ""
              }`}
            >
              {stepName === "basic"
                ? "Basic Info"
                : stepName.charAt(0).toUpperCase() + stepName.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-lg shadow p-6">
        {renderStepContent()}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8">
          <button
            type="button"
            onClick={handlePrevious}
            disabled={step === "basic"}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>

          {step === "review" ? (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading
                ? _quizId
                  ? "Updating..."
                  : "Creating..."
                : _quizId
                ? "Update Quiz"
                : "Create Quiz"}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleNext}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuizCreator;
